<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ConversationApi from 'dashboard/api/inbox/conversation';

// Phase G4 — moderation toolbar rendered under each incoming
// customer-comment Message. Calls the Phase G3 REST endpoints:
//   POST /conversations/:cid/messages/:mid/hide_comment
//   POST /conversations/:cid/messages/:mid/unhide_comment
//   POST /conversations/:cid/messages/:mid/delete_comment
//   POST /conversations/:cid/messages/:mid/like_comment
//
// Mounted by components-next/message/Message.vue gated on
// contentAttributes.commentMeta.commentId + messageType === INCOMING.
const props = defineProps({
  conversationId: { type: Number, required: true },
  messageId: { type: Number, required: true },
  // Backend Channel type, e.g. 'Channel::Instagram'. Used to hide the
  // Like button on IG (Meta Graph API doesn't expose IG comment likes;
  // Phase G2 service returns 'not supported' anyway).
  channelType: { type: String, default: null },
  // Optional initial hidden state read from content_attributes.
  // Phase A ingestion does NOT capture is_hidden yet — so this defaults
  // to false and only flips after a successful hide/unhide click.
  initialHidden: { type: Boolean, default: false },
});

const { t } = useI18n();
const isProcessing = ref(false);
const localHidden = ref(props.initialHidden);

const isInstagram = computed(() => props.channelType === 'Channel::Instagram');

// Refresh-on-open: ask Meta for the comment's current hidden state so the
// toolbar reflects hides made from any source (Meta Business Suite,
// auto-moderation filters, etc.), not just hides done through Chatwoot.
// Non-fatal — on error we keep initialHidden so the toolbar still renders.
onMounted(async () => {
  try {
    const { data } = await ConversationApi.commentHiddenState(
      props.conversationId,
      props.messageId
    );
    if (typeof data?.is_hidden === 'boolean') {
      localHidden.value = data.is_hidden;
    }
  } catch (err) {
    // keep initialHidden
  }
});

async function perform(action, confirmText = null) {
  if (isProcessing.value) return;
  if (confirmText && !window.confirm(confirmText)) return;
  isProcessing.value = true;
  try {
    const fnName = `${action}Comment`;
    const fn = ConversationApi[fnName];
    if (typeof fn !== 'function') {
      throw new Error(`api method ${fnName} not found`);
    }
    await fn.call(ConversationApi, props.conversationId, props.messageId);
    useAlert(t(`CONVERSATION.MODERATE.${action.toUpperCase()}_SUCCESS`));
    if (action === 'hide') localHidden.value = true;
    if (action === 'unhide') localHidden.value = false;
  } catch (err) {
    const detail =
      err?.response?.data?.error || err?.message || 'unknown';
    useAlert(
      `${t(`CONVERSATION.MODERATE.${action.toUpperCase()}_FAILURE`)}: ${detail}`,
      { type: 'error' }
    );
  } finally {
    isProcessing.value = false;
  }
}

const hide = () => perform('hide');
const unhide = () => perform('unhide');
const del = () => perform('delete', t('CONVERSATION.MODERATE.DELETE_CONFIRM_BODY'));
const like = () => perform('like');
</script>

<template>
  <div class="comment-moderation-toolbar">
    <button
      v-if="!localHidden"
      type="button"
      class="btn-hide"
      :disabled="isProcessing"
      @click="hide"
    >
      {{ $t('CONVERSATION.MODERATE.HIDE') }}
    </button>
    <button
      v-if="localHidden"
      type="button"
      class="btn-unhide"
      :disabled="isProcessing"
      @click="unhide"
    >
      {{ $t('CONVERSATION.MODERATE.UNHIDE') }}
    </button>
    <button
      type="button"
      class="btn-delete"
      :disabled="isProcessing"
      @click="del"
    >
      {{ $t('CONVERSATION.MODERATE.DELETE') }}
    </button>
    <button
      v-if="!isInstagram"
      type="button"
      class="btn-like"
      :disabled="isProcessing"
      @click="like"
    >
      {{ $t('CONVERSATION.MODERATE.LIKE') }}
    </button>
    <span v-if="localHidden" class="hidden-label">
      {{ $t('CONVERSATION.MODERATE.HIDDEN_LABEL') }}
    </span>
  </div>
</template>

<style lang="scss" scoped>
// Phase C + D3 found that bg-slate-*/bg-violet-* tailwind utilities
// aren't available in Chatwoot's tailwind config — use raw hex via
// scoped SCSS, same idiom as ReplyBox.vue hand-to-bot-btn.
.comment-moderation-toolbar {
  @apply flex gap-1 mt-1 text-xs;
}

.btn-hide,
.btn-unhide,
.btn-delete,
.btn-like {
  @apply px-2 py-1 rounded font-medium;
}

.btn-hide,
.btn-unhide {
  background-color: #f1f5f9; // slate-100
  color: #334155; // slate-700
  &:hover:not(:disabled) {
    background-color: #e2e8f0; // slate-200
  }
}

.btn-delete {
  background-color: #fee2e2; // rose-100
  color: #b91c1c; // rose-700
  &:hover:not(:disabled) {
    background-color: #fecaca; // rose-200
  }
}

.btn-like {
  background-color: #dbeafe; // blue-100
  color: #1d4ed8; // blue-700
  &:hover:not(:disabled) {
    background-color: #bfdbfe; // blue-200
  }
}

button[disabled] {
  @apply opacity-50 cursor-not-allowed;
}

.hidden-label {
  @apply px-2 py-1 font-medium italic;
  color: #64748b; // slate-500
}
</style>
